// ============================================================
// PERRY — SPECIES SELECT
// One starter from each of the four Ancestries: Humanoid/Space,
// Beast/Phantom, Machine/Time, Dragon/Dragon. The hero is always
// named Perry — you are choosing what Perry IS.
//
// The other eight of the twelve are roadmapped; these four are
// the Anchors, one per Ancestry, so the tale reads the same shape
// whichever one you pick.
// ============================================================

import { useState, useEffect } from "react";
import { GM } from "./theme";
import { EmberDust, MetalRule, ensureKeyframes } from "./PerryUI";
import { SPECIES, SPECIES_ORDER } from "./perryData";

const KF = `
@keyframes spIn   { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes spHalo { 0%,100% { opacity: 0.35; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.06); } }
`;

// A tiny 2D portrait of each beb, drawn in SVG so nothing loads.
function BebGlyph({ sp, size = 92 }) {
  const s = size;
  return (
    <svg width={s} height={s} viewBox="0 0 100 100">
      <defs>
        <radialGradient id={`g_${sp.id}`} cx="50%" cy="38%">
          <stop offset="0%" stopColor={sp.coatHot} />
          <stop offset="65%" stopColor={sp.coat} />
          <stop offset="100%" stopColor="#0a0605" />
        </radialGradient>
      </defs>
      {/* aura */}
      <circle cx="50" cy="52" r="40" fill={sp.glow} opacity="0.10" />
      {/* body */}
      <ellipse cx="50" cy="72" rx="21" ry="17" fill={`url(#g_${sp.id})`} stroke="#0c0806" strokeWidth="2" />
      {/* head */}
      <circle cx="50" cy="45" r="21" fill={`url(#g_${sp.id})`} stroke="#0c0806" strokeWidth="2" />
      {/* species signature */}
      {sp.id === "shadow_fox" && (
        <>
          <path d="M33 30 L30 12 L44 24 Z" fill={sp.coat} stroke="#0c0806" strokeWidth="2" />
          <path d="M67 30 L70 12 L56 24 Z" fill={sp.coat} stroke="#0c0806" strokeWidth="2" />
        </>
      )}
      {sp.id === "chaos_dragon" && (
        <>
          <path d="M38 27 L32 12 L46 22 Z" fill="#e8d8c8" stroke="#0c0806" strokeWidth="1.6" />
          <path d="M62 27 L68 12 L54 22 Z" fill="#e8d8c8" stroke="#0c0806" strokeWidth="1.6" />
          <path d="M72 62 C86 52 94 58 96 68 C86 72 76 70 72 64 Z" fill={sp.coat} stroke="#0c0806" strokeWidth="1.6" />
        </>
      )}
      {sp.id === "antdroid" && (
        <>
          <path d="M40 27 C36 16 32 12 28 10" fill="none" stroke="#3a2a18" strokeWidth="2.4" />
          <path d="M60 27 C64 16 68 12 72 10" fill="none" stroke="#3a2a18" strokeWidth="2.4" />
          <circle cx="28" cy="10" r="4" fill={sp.eye} />
          <circle cx="72" cy="10" r="4" fill={sp.eye} />
        </>
      )}
      {sp.id === "h01" && (
        <ellipse cx="50" cy="21" rx="17" ry="5" fill="none" stroke={sp.glow} strokeWidth="2.2" opacity="0.85" />
      )}
      {/* eyes */}
      <ellipse cx="42" cy="45" rx="5.4" ry="6.2" fill="#0a0604" />
      <ellipse cx="58" cy="45" rx="5.4" ry="6.2" fill="#0a0604" />
      <circle cx="42" cy="44" r="3.4" fill={sp.eye} />
      <circle cx="58" cy="44" r="3.4" fill={sp.eye} />
      <circle cx="40.5" cy="42.5" r="1.3" fill="#fff" />
      <circle cx="56.5" cy="42.5" r="1.3" fill="#fff" />
      {/* paws */}
      <circle cx="31" cy="76" r="6" fill={sp.coatHot} stroke="#0c0806" strokeWidth="1.6" />
      <circle cx="69" cy="76" r="6" fill={sp.coatHot} stroke="#0c0806" strokeWidth="1.6" />
    </svg>
  );
}

export default function SpeciesSelect({ onChoose, onBack }) {
  ensureKeyframes("perry-species-keyframes", KF);
  const [sel, setSel] = useState("h01");
  const sp = SPECIES[sel];

  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat) return;
      const i = SPECIES_ORDER.indexOf(sel);
      if (e.key === "ArrowRight" || e.key.toUpperCase() === "D") setSel(SPECIES_ORDER[(i + 1) % 4]);
      if (e.key === "ArrowLeft" || e.key.toUpperCase() === "A") setSel(SPECIES_ORDER[(i + 3) % 4]);
      if (e.key === "Enter") onChoose(sel);
      if (e.key === "Escape") onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel, onChoose, onBack]);

  return (
    <div style={{
      position: "absolute", inset: 0, background: "#050302",
      overflow: "auto", fontFamily: GM.fontUI,
    }}>
      <div style={{ position: "absolute", inset: 0, background: GM.candlelight }} />
      <EmberDust count={16} />

      <div style={{
        position: "relative", minHeight: "100%", display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "40px 20px",
      }}>
        <div style={{ textAlign: "center", marginBottom: 4 }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: 28, letterSpacing: 8, color: GM.vellum,
            textShadow: `0 0 30px ${GM.inkGold}`,
          }}>WHAT IS PERRY?</div>
          <MetalRule width={240} style={{ margin: "10px auto 8px" }} />
          <div style={{ fontSize: 10, letterSpacing: 4, color: GM.vellumFaint, marginBottom: 22 }}>
            ONE ANCHOR FROM EACH ANCESTRY · THE NAME NEVER CHANGES
          </div>
        </div>

        <div style={{
          display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center",
          marginBottom: 22, maxWidth: 720,
        }}>
          {SPECIES_ORDER.map((id, i) => {
            const s = SPECIES[id];
            const on = id === sel;
            return (
              <button key={id} onClick={() => setSel(id)} style={{
                position: "relative", width: 150, padding: "16px 10px 14px",
                borderRadius: 12, cursor: "pointer",
                background: on
                  ? "linear-gradient(175deg, #2e1b0e 0%, #1a0f08 100%)"
                  : "linear-gradient(175deg, #150c07 0%, #0b0605 100%)",
                border: `1px solid ${on ? s.glow : GM.pageBorder}`,
                boxShadow: on
                  ? `0 10px 26px rgba(0,0,0,0.6), 0 0 30px ${s.glow}33`
                  : "0 4px 12px rgba(0,0,0,0.5)",
                transition: "all 0.18s ease",
                animation: `spIn 0.5s ease-out ${i * 0.07}s backwards`,
              }}>
                {on && (
                  <span style={{
                    position: "absolute", inset: -2, borderRadius: 13,
                    border: `1px solid ${s.glow}`, animation: "spHalo 2s ease-in-out infinite",
                    pointerEvents: "none",
                  }} />
                )}
                <BebGlyph sp={s} size={88} />
                <div style={{
                  marginTop: 6, fontFamily: GM.font, fontSize: 12, letterSpacing: 2,
                  color: on ? "#ffe6c2" : GM.vellumDim,
                }}>{s.name.replace(" Beb", "").toUpperCase()}</div>
                <div style={{ fontSize: 9, letterSpacing: 1, color: GM.vellumFaint, marginTop: 2 }}>
                  {s.ancestry.toUpperCase()}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── the chosen one ── */}
        <div style={{
          width: "min(560px, 92vw)", padding: "18px 22px", borderRadius: 12,
          background: "linear-gradient(175deg, #1c1009, #0d0705)",
          border: `1px solid ${GM.pageBorder}`, textAlign: "center",
        }}>
          <div style={{
            fontFamily: GM.fontBody, fontSize: 17, lineHeight: 1.6, color: GM.vellum,
          }}>{sp.tagline}</div>
          <div style={{
            marginTop: 12, fontSize: 12, letterSpacing: 1, color: sp.glow,
            textShadow: `0 0 12px ${sp.glow}66`,
          }}>✦ {sp.perk}</div>
        </div>

        <button onClick={() => onChoose(sel)} style={{
          marginTop: 22, padding: "14px 52px", borderRadius: 11, cursor: "pointer",
          background: "linear-gradient(175deg, #a8601e 0%, #7a4218 55%, #4a2810 100%)",
          border: `1px solid ${GM.inkGold}`, color: "#ffe6c2",
          fontFamily: GM.font, fontSize: 15, letterSpacing: 6, fontWeight: 700,
          boxShadow: `0 8px 22px rgba(0,0,0,0.6), 0 0 26px ${GM.inkGoldDim}`,
        }}>WALK OUT OF REEDBROOK</button>

        <div onClick={onBack} style={{
          marginTop: 14, fontSize: 11, letterSpacing: 3, color: GM.vellumFaint, cursor: "pointer",
        }}>← BACK</div>
      </div>
    </div>
  );
}
